'use client';

import {Trans} from 'react-i18next';
import {useI18n} from '@/lib/i18n/client';
import Image from 'next/image';
import GlowingCircle from '@/components/GlowingCircle';
import Badge from '@/components/Badge';
import LifeAgentMark from '@/components/LifeAgentMark';
import {useSearchParams} from 'next/navigation';
import {Suspense, useEffect, useRef, useState} from 'react';
import {store, useUserFlow} from '@/lib/UserFlow';
import '@/components/NFC/MockNDEFReader';
import {useDpr} from '@/lib/useDpr';
import {deriveTestTagUid} from '@/lib/testTagUid';

function IdleContent() {
  const { t } = useI18n();
  const searchParams = useSearchParams();
  const { stageCompleted } = useUserFlow();
  const dpr = useDpr();

  const rootRef = useRef<HTMLDivElement>(null);
  const badgeAnchorRef = useRef<HTMLSpanElement>(null);
  const circleRef = useRef<HTMLDivElement>(null);
  const [arrow, setArrow] = useState<{
    d: string;
    tipX: number;
    tipY: number;
    angle: number;
  } | null>(null);

  // Arrow geometry/style constants (dialed in via a former ?tune slider panel).
  const params = DEFAULT_ARROW_PARAMS;

  useEffect(() => {
    const testUser = searchParams.get('test-user');
    if (!testUser) return;
    deriveTestTagUid(testUser).then((nfcTagUid) => {
      store.trigger.startSession({ user: { userUid: testUser, nfcTagUid } });
    });
  }, [searchParams]);

  // Curved connector: measure the badge anchor and the circle, then build a
  // quadratic Bézier from the anchor to a point on the ring, bowing toward
  // screen-right (away from the left-aligned text). Geometry comes from
  // `params` (tunable via ?tune). Recomputed on resize / dpr / param change so
  // the curve always connects regardless of screen size.
  useEffect(() => {
    const root = rootRef.current;
    const badge = badgeAnchorRef.current;
    const circle = circleRef.current;
    if (!root || !badge || !circle) return;

    const { curvature, backoff, ringFactor, targetAngle } = params;

    const compute = () => {
      const r = root.getBoundingClientRect();
      const b = badge.getBoundingClientRect();
      const c = circle.getBoundingClientRect();
      // Circle is max-lg:hidden (0×0) on small screens — no arrow then.
      if (c.width === 0 || c.height === 0) {
        setArrow(null);
        return;
      }
      const sx = b.left + b.width / 2 - r.left;
      const sy = b.top + b.height / 2 - r.top;
      const cx = c.left + c.width / 2 - r.left;
      const cy = c.top + c.height / 2 - r.top;
      // Target point on the ring at `targetAngle` (screen degrees, y-down;
      // 135° = south-west). ringFactor·diameter ≈ ring radius.
      const ringRadius = c.width * ringFactor;
      const rad = (targetAngle * Math.PI) / 180;
      const ex = cx + ringRadius * Math.cos(rad);
      const ey = cy + ringRadius * Math.sin(rad);
      // Control point: span midpoint pushed along the perpendicular toward
      // screen-right (+x), away from the left-aligned heading/text. Offset
      // scales with span length so the curvature looks identical at any
      // screen size.
      const mx = (sx + ex) / 2;
      const my = (sy + ey) / 2;
      const dx = ex - sx;
      const dy = ey - sy;
      const dlen = Math.hypot(dx, dy) || 1;
      let px = -dy / dlen;
      let py = dx / dlen;
      if (px < 0) {
        px = -px;
        py = -py;
      }
      const cpx = mx + px * dlen * curvature;
      const cpy = my + py * dlen * curvature;
      // Curve end tangent (quadratic derivative at t=1 ∝ P2 − P1) → arrowhead
      // angle. Arrowhead stays at the true SW point (ex, ey).
      const endVx = ex - cpx;
      const endVy = ey - cpy;
      const endLen = Math.hypot(endVx, endVy) || 1;
      const angle = (Math.atan2(endVy, endVx) * 180) / Math.PI;
      // Trim the dotted stroke by splitting the SAME Bézier at t (De
      // Casteljau), so the line stops ON the curve just before the arrowhead
      // instead of detaching. backoff ≈ arc length left for the arrowhead;
      // end speed ≈ 2·|P2−P1|, so 1−t ≈ backoff / endSpeed.
      // Ceiling near 1 (not 0.95 — that pinned the trim and made `backoff`
      // a no-op); floor at 0 guards a very short span going negative.
      const tEnd = Math.min(Math.max(1 - backoff / (2 * endLen), 0), 0.999);
      const ax = sx + (cpx - sx) * tEnd;
      const ay = sy + (cpy - sy) * tEnd;
      const bx = cpx + (ex - cpx) * tEnd;
      const by = cpy + (ey - cpy) * tEnd;
      const trimX = ax + (bx - ax) * tEnd;
      const trimY = ay + (by - ay) * tEnd;
      setArrow({
        d: `M ${sx} ${sy} Q ${ax} ${ay} ${trimX} ${trimY}`,
        tipX: ex,
        tipY: ey,
        angle,
      });
    };

    compute();
    const onResize = () => requestAnimationFrame(compute);
    const ro = new ResizeObserver(onResize);
    ro.observe(root);
    ro.observe(badge);
    ro.observe(circle);
    window.addEventListener('resize', onResize);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [dpr, params]);

  const handleGlowingCircleClick = () => {
    const testUser = searchParams.get('test-user');
    if (testUser) {
      stageCompleted();
    }
  };

  return (
    <div ref={rootRef} className="relative h-screen w-full overflow-hidden text-center">

      {/* Phaethon Logo Watermark - Top Center */}
      <div className="flex justify-center opacity-20">
        <div className="relative w-[26%] aspect-[391/277]">
          <Image
            src="/images/phaethon-logo.png"
            alt={t('home.phaethon_logo_alt')}
            fill
            className="object-contain"
            priority
          />
        </div>
      </div>

      <div className="text-left px-32 pt-12">

        {/* Main Heading */}
        <h1 className="text-display-xl ml-[-5px]">
          <Trans i18nKey="home.heading" components={{lifeAgent: <LifeAgentMark />, br: <br />}} />
        </h1>

        {/* Scan Prompt Subheading */}
        <p className="text-input mt-10">
          <Trans i18nKey="home.scan_prompt" components={{lifeAgent: <LifeAgentMark />}} />
        </p>
      </div>

      {/* NFC Reader Area with Glowing Circle - Positioned at specified coordinates */}
      <div
        ref={circleRef}
        className="absolute overflow-visible max-lg:hidden"
        style={{
          left: `${1547 / dpr}px`,
          top: `${535 / dpr}px`,
          transform: 'translate(-50%, -50%)',
        }}
      >
        <GlowingCircle size="lg" duration={3} color="#49FCFE" className="overflow-visible" onClick={handleGlowingCircleClick}>
          {/* Concentric rings + label inside the glow. Exact Figma export: a
              30px black and a 20px #49FCFE stroke on the same r=167.357
              centerline (reads as black 5 / teal 20 / black 5). CSS borders
              couldn't render this cleanly, so the SVG is used verbatim. Its
              viewBox is 365 ≈ Ellipse 6's outer Ø (364.714); sized as a
              fraction of --size-circle-lg (the glow / Ellipse 1 ≈ 630px) so
              the badge scales with the kiosk's breakpoint tokens. */}
          <div
            className="relative flex items-center justify-center"
            style={{
              width: 'calc(var(--size-circle-lg) * 0.579)',
              height: 'calc(var(--size-circle-lg) * 0.579)',
            }}
          >
            <svg
              className="absolute inset-0 h-full w-full"
              viewBox="0 0 365 365"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <circle cx="182.357" cy="182.357" r="167.357" stroke="black" strokeWidth={30} />
              <circle cx="182.357" cy="182.357" r="167.357" stroke="#49FCFE" strokeWidth={20} />
            </svg>
            {/* Cap to the ring's safe inner width so longer locales (fr/es)
                wrap instead of crowding the ring; "HERE/ICI" stays prominent. */}
            <div
              className="relative flex flex-col items-center text-center font-semibold text-phaethon-text text-balance"
              style={{ maxWidth: 'calc(var(--size-circle-lg) * 0.42)' }}
            >
              <span
                style={{
                  fontSize: 'calc(var(--size-circle-lg) * 0.057)',
                  lineHeight: 1.1,
                }}
              >
                {t('home.badge_hold')}
              </span>
              <span
                style={{
                  fontSize: 'calc(var(--size-circle-lg) * 0.143)',
                  lineHeight: 'calc(var(--size-circle-lg) * 0.135)',
                }}
              >
                {t('home.badge_here')}
              </span>
            </div>
          </div>
        </GlowingCircle>
      </div>

      {/* Curved dotted arrow: a full-screen overlay whose Bézier path is
          measured (effect above) from the badge-card edge to the ring edge.
          pointer-events-none so it never blocks the circle's click target. */}
      {arrow ? (
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full text-phaethon-text max-xl:hidden"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <path
            d={arrow.d}
            stroke="currentColor"
            strokeWidth={params.strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${params.dotSize} ${params.dotGap}`}
          />
          <path
            d={`M0 0 L${params.markerLen} ${params.markerLen / 2} L0 ${params.markerLen} z`}
            fill="currentColor"
            transform={`translate(${arrow.tipX} ${arrow.tipY}) rotate(${arrow.angle}) translate(${-params.markerLen} ${-params.markerLen / 2})`}
          />
        </svg>
      ) : null}

      {/* Badge: decorative, bottom-center. The 0-size anchor marks the badge's
          right edge (mid-height) — the curve's start point. */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 max-xl:hidden">
        <Badge size={120} />
        <span
          ref={badgeAnchorRef}
          className="absolute"
          style={{ left: `${params.anchorX}%`, top: `${params.anchorY}%` }}
        />
      </div>

    </div>
  );
}

export default function IdleScreen() {
  return (
    <Suspense fallback={<div className="relative h-screen w-full overflow-hidden" />}>
      <IdleContent />
    </Suspense>
  );
}

interface ArrowParams {
  /** Bézier bow strength (perpendicular offset = curvature · span length) */
  curvature: number;
  /** Arc length (px) the dotted line stops short of the arrowhead */
  backoff: number;
  /** Ring target radius as a fraction of the circle's diameter */
  ringFactor: number;
  /** Ring target angle in screen degrees (y-down; 135 = south-west) */
  targetAngle: number;
  /** Curve start X within the Badge box (%) */
  anchorX: number;
  /** Curve start Y within the Badge box (%) */
  anchorY: number;
  /** Dotted stroke: dash (dot) length */
  dotSize: number;
  /** Dotted stroke: gap between dots */
  dotGap: number;
  /** Dotted stroke width */
  strokeWidth: number;
  /** Arrowhead triangle length */
  markerLen: number;
}

const DEFAULT_ARROW_PARAMS: ArrowParams = {
  curvature: 0.26,
  backoff: 26,
  ringFactor: 0.32,
  targetAngle: 128,
  anchorX: 111,
  anchorY: 60,
  dotSize: 0.5,
  dotGap: 20,
  strokeWidth: 5.5,
  markerLen: 24,
};
